import React, { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import { PatientAPI, PatientApiError } from "../../services/patient.service";
import type { PatientDbRecord } from "../../schemas/patient.schema";
import { indianMobileRegex } from "../../schemas/patient.schema";
import { hasFieldChanged } from "../../config/patientFieldMapping";
import { putAndQueue } from "../../db/operations";
import type { PatientRecord } from "../../db/db.types";
import { isBackendUnreachable, isPermissionDenied, actionErrorMessage } from "../../utils/network";
import { logger } from "../../utils/logger";
import { useRole } from "../../hooks/useRole";
import {
  ChevronDownIcon,
  BasicDetailsIcon,
  ContactInfoIcon,
  AddressIcon,
} from "../icons";
import { DateInput } from "../ui/DateInput";
import AllergiesPanel from "../AllergiesPanel";

interface EditPatientDrawerProps {
  patientId: string;
  patient: PatientDbRecord;
  isOpen: boolean;
  onClose: () => void;
  onSaved: (updated: PatientDbRecord) => void;
}

type TabId = "personal" | "family" | "medical" | "allergies";

const TABS: Array<{ id: TabId; label: string }> = [
  { id: "personal", label: "Personal Information" },
  { id: "family", label: "Family & Emergency" },
  { id: "medical", label: "Medical Information" },
  { id: "allergies", label: "Allergies" },
];

const INDIAN_STATES = [
  "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh", "Goa",
  "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka", "Kerala",
  "Madhya Pradesh", "Maharashtra", "Manipur", "Meghalaya", "Mizoram", "Nagaland",
  "Odisha", "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu", "Telangana", "Tripura",
  "Uttar Pradesh", "Uttarakhand", "West Bengal", "Andaman and Nicobar Islands",
  "Chandigarh", "Dadra and Nagar Haveli and Daman and Diu", "Delhi",
  "Jammu and Kashmir", "Ladakh", "Lakshadweep", "Puducherry",
];

const DEFAULT_LIFESTYLE_OPTIONS = [
  { id: 1, label: "Smoker", selected: false },
  { id: 2, label: "Regular Drinker", selected: false },
  { id: 3, label: "Obese", selected: false },
  { id: 4, label: "High Stress", selected: false },
];

const validateEmail = (email: string): string => {
  if (!email) return "";
  if (!email.includes("@")) return "Email must contain '@' symbol";
  const parts = email.split("@");
  if (parts.length !== 2 || !parts[0] || !parts[1]) return "Invalid email format";
  if (!parts[1].includes(".")) return "Email must contain a domain with '.' (e.g., example.com)";
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) return "Invalid email format";
  return "";
};

const lookupPincode = async (
  pin: string,
  setCity: (v: string) => void,
  setState: (v: string) => void
) => {
  if (pin.length !== 6) return;
  try {
    const res = await fetch(`https://api.postalpincode.in/pincode/${pin}`);
    const data = await res.json();
    if (data?.[0]?.Status === "Success" && data[0].PostOffice?.length > 0) {
      const po = data[0].PostOffice[0];
      setCity(po.Division || po.District || "");
      setState(po.State || "");
    }
  } catch {
    // silently ignore — user can fill manually
  }
};

const EditPatientDrawer: React.FC<EditPatientDrawerProps> = ({
  patientId,
  patient,
  isOpen,
  onClose,
  onSaved,
}) => {
  const { canOr } = useRole();
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<TabId>("personal");
  const [patientData, setPatientData] = useState<PatientDbRecord>(patient);
  const [originalData, setOriginalData] = useState<any>(null);

  const [formData, setFormData] = useState({
    title: "", name: "", first_name: "", last_name: "", dateOfBirth: "",
    maritalStatus: "", bloodGroup: "", company: "", gender: "", occupation: "",
    mobileNumber: "", email: "", alternateNumber: "", address: "", city: "",
    state: "", pincode: "", abhaAddress: "", aadhar_number: "",
  });

  const [emergencyContact, setEmergencyContact] = useState({
    name: "", relationship: "", email: "", mobileNumber: "", address: "",
    city: "", state: "", pincode: "", isGuardian: false, guardianWhatsappConsent: false,
  });
  // Non-null while editing an already-saved contact in place, rather than
  // adding a new one — set by clicking the pencil icon on a saved contact.
  const [editingContactId, setEditingContactId] = useState<number | null>(null);

  const [emergencyContacts, setEmergencyContacts] = useState<Array<{
    id: number; name: string; relationship: string; email: string;
    mobileNumber: string; address: string; city: string; state: string; pincode: string;
    isGuardian: boolean; guardianWhatsappConsent: boolean;
  }>>([]);

  const [insuranceData, setInsuranceData] = useState({
    provider: "", policyNumber: "", policyHolderName: "", policyExpiryDate: "", coverageAmount: "",
  });

  const [medicalConditions, setMedicalConditions] = useState<Array<{
    id: number; name: string; checked: boolean;
  }>>([]);

  const [currentMedications, setCurrentMedications] = useState("");
  const [previousSurgeries, setPreviousSurgeries] = useState("");
  const [showAddCondition, setShowAddCondition] = useState(false);
  const [newConditionName, setNewConditionName] = useState("");

  const [lifestyleOptions, setLifestyleOptions] = useState(DEFAULT_LIFESTYLE_OPTIONS);

  const [notifications, setNotifications] = useState({
    paymentReceived: false, systemUpdates: false, appointmentConfirmations: true,
    appointmentReminders: true, prescriptionReady: true, paymentReminders: true,
  });

  const [emailSettings, setEmailSettings] = useState({
    frequency: "Instant", quietHours: "10 PM - 8 AM",
  });

  const [personalEmailError, setPersonalEmailError] = useState("");
  const [emergencyEmailError, setEmergencyEmailError] = useState("");

  // Only meaningful/editable for pediatric-age patients (see isPediatricAge below).
  const [pediatricData, setPediatricData] = useState({
    gestationalAgeWeeks: "",
    gestationalAgeDays: "",
    birthWeightGrams: "",
    isPreterm: false,
    multipleBirth: false,
    isDownSyndrome: false,
  });
  const [pediatricError, setPediatricError] = useState<string | null>(null);

  const isPediatricAge = (() => {
    if (!formData.dateOfBirth) return false;
    const dob = new Date(formData.dateOfBirth);
    if (isNaN(dob.getTime())) return false;
    const ageMs = Date.now() - dob.getTime();
    return ageMs / (365.25 * 24 * 60 * 60 * 1000) < 5;
  })();

  // Snapshot captured when the drawer opens, used to restore the form on Cancel.
  const editSnapshotRef = useRef<{
    formData: typeof formData;
    emergencyContact: typeof emergencyContact;
    emergencyContacts: typeof emergencyContacts;
    insuranceData: typeof insuranceData;
    medicalConditions: typeof medicalConditions;
    currentMedications: string;
    previousSurgeries: string;
    lifestyleOptions: typeof lifestyleOptions;
    notifications: typeof notifications;
    emailSettings: typeof emailSettings;
    pediatricData: typeof pediatricData;
  } | null>(null);

  // Populate every editable field from the `patient` prop each time the drawer
  // opens — no independent fetch, the profile page already holds this data.
  useEffect(() => {
    if (!isOpen) return;
    const data = patient;
    setPatientData(data);
    setActiveTab("personal");
    setShowAddCondition(false);
    setNewConditionName("");
    setPersonalEmailError("");
    setEmergencyEmailError("");

    setOriginalData({
      patient: {
        title: data.title, first_name: data.first_name, last_name: data.last_name,
        date_of_birth: data.date_of_birth, gender: data.gender, blood_group: data.blood_group,
        marital_status: data.marital_status, occupation: data.occupation, company: data.company,
      },
      contact: data.contact ? { ...data.contact } : null,
      address: data.address ? { ...data.address } : null,
      insurance: data.insurance ? { ...data.insurance } : null,
      medical_profile: data.medical_profile ? { ...data.medical_profile } : null,
      pediatric_profile: (data as any).pediatric_profile ? { ...(data as any).pediatric_profile } : null,
      preferences: data.preferences ? { ...data.preferences } : null,
      emergency_contacts: data.emergency_contacts ? JSON.parse(JSON.stringify(data.emergency_contacts)) : [],
    });

    const contactData: Record<string, any> = data.contact || {};
    const addressData: Record<string, any> = data.address || {};

    const nextFormData = {
      title: data.title || "",
      name: (data as any).name || `${data.first_name} ${data.last_name}`,
      first_name: data.first_name || "",
      last_name: data.last_name || "",
      dateOfBirth: data.date_of_birth || "",
      maritalStatus: data.marital_status || "",
      bloodGroup: data.blood_group || "",
      company: data.company || "",
      gender: data.gender || "",
      occupation: data.occupation || "",
      mobileNumber: contactData.contact_number?.replace("+91", "") || "",
      email: contactData.email || "",
      alternateNumber: contactData.alternate_contact_number?.replace("+91", "") || "",
      address: addressData.address || "",
      city: addressData.city || "",
      state: addressData.state || "",
      pincode: addressData.pincode || "",
      abhaAddress: contactData.abha_id || "",
      aadhar_number: contactData.aadhar_number || "",
    };
    setFormData(nextFormData);

    const insuranceObj: Record<string, any> = data.insurance || {};
    const nextInsuranceData = {
      provider: insuranceObj.provider || "",
      policyNumber: insuranceObj.policy_number || "",
      policyHolderName: insuranceObj.policy_holder_name || "",
      policyExpiryDate: insuranceObj.policy_expiry_date || "",
      coverageAmount: insuranceObj.coverage_amount ? insuranceObj.coverage_amount.toString() : "",
    };
    setInsuranceData(nextInsuranceData);

    let nextEmergencyContacts: typeof emergencyContacts = [];
    const emptyEmergencyContact = {
      name: "", relationship: "", email: "", mobileNumber: "", address: "", city: "", state: "", pincode: "",
      isGuardian: false, guardianWhatsappConsent: false,
    };
    if (data.emergency_contacts && Array.isArray(data.emergency_contacts) && data.emergency_contacts.length > 0) {
      nextEmergencyContacts = data.emergency_contacts.map((contact: any, index: number) => ({
        id: index + 1,
        name: contact.name || "",
        relationship: contact.relationship || "Emergency Contact",
        email: contact.email || "",
        mobileNumber: contact.contact_number?.replace("+91", "") || "",
        address: contact.address || "",
        city: contact.city || "",
        state: contact.state || "",
        pincode: contact.pincode || "",
        isGuardian: contact.is_guardian || false,
        guardianWhatsappConsent: contact.guardian_whatsapp_consent || false,
      }));
    }
    setEmergencyContacts(nextEmergencyContacts);
    setEmergencyContact(emptyEmergencyContact);

    const medicalProfile: Record<string, any> = data.medical_profile || {};
    setCurrentMedications(medicalProfile.current_medications || "");
    setPreviousSurgeries(medicalProfile.previous_surgeries || "");

    let nextMedicalConditions: typeof medicalConditions = [];
    if (medicalProfile.medical_conditions) {
      try {
        const conditions = typeof medicalProfile.medical_conditions === "string"
          ? JSON.parse(medicalProfile.medical_conditions)
          : medicalProfile.medical_conditions;
        if (Array.isArray(conditions) && conditions.length > 0) {
          if (typeof conditions[0] === "object" && conditions[0] !== null) {
            nextMedicalConditions = conditions.flatMap((conditionObj: any, index: number) =>
              Object.entries(conditionObj).map(([name, status], subIndex) => ({
                id: index * 100 + subIndex + 1,
                name,
                checked: status === "Yes",
              }))
            );
          } else {
            nextMedicalConditions = conditions.map((name: string, index: number) => ({
              id: index + 1, name, checked: true,
            }));
          }
        }
      } catch (e) {
        logger.error("Error parsing medical conditions", e instanceof Error ? e : undefined);
      }
    }
    setMedicalConditions(nextMedicalConditions);

    let nextLifestyleOptions = DEFAULT_LIFESTYLE_OPTIONS;
    if (medicalProfile.lifestyle_options) {
      try {
        const lifestyle = typeof medicalProfile.lifestyle_options === "string"
          ? JSON.parse(medicalProfile.lifestyle_options)
          : medicalProfile.lifestyle_options;
        if (Array.isArray(lifestyle)) {
          nextLifestyleOptions = DEFAULT_LIFESTYLE_OPTIONS.map(option => ({
            ...option, selected: lifestyle.includes(option.label),
          }));
        } else if (typeof lifestyle === "object" && lifestyle !== null) {
          const lifestyleLabels: Record<string, string> = {
            smoking: lifestyle.smoking === "Yes" ? "Smoker" : "",
            alcohol: lifestyle.alcohol === "Yes" ? "Regular Drinker" : "",
          };
          nextLifestyleOptions = DEFAULT_LIFESTYLE_OPTIONS.map(option => ({
            ...option,
            selected: Object.values(lifestyleLabels).filter(Boolean).includes(option.label),
          }));
        }
      } catch (e) {
        logger.error("Error parsing lifestyle options", e instanceof Error ? e : undefined);
      }
    }
    setLifestyleOptions(nextLifestyleOptions);

    const preferencesObj: Record<string, any> = data.preferences || {};
    const nextNotifications = {
      paymentReceived: preferencesObj.payment_received === true,
      systemUpdates: preferencesObj.system_updates === true,
      appointmentConfirmations: preferencesObj.appointment_confirmations === true,
      appointmentReminders: preferencesObj.appointment_reminders === true,
      prescriptionReady: preferencesObj.prescription_ready === true,
      paymentReminders: preferencesObj.payment_reminders === true,
    };
    setNotifications(nextNotifications);

    const nextEmailSettings = {
      frequency: preferencesObj.email_frequency || "Instant",
      quietHours: preferencesObj.quiet_hours || "10 PM - 8 AM",
    };
    setEmailSettings(nextEmailSettings);

    const pediatricObj: Record<string, any> = (data as any).pediatric_profile || {};
    const nextPediatricData = {
      gestationalAgeWeeks: pediatricObj.gestational_age_weeks != null ? pediatricObj.gestational_age_weeks.toString() : "",
      gestationalAgeDays: pediatricObj.gestational_age_days != null ? pediatricObj.gestational_age_days.toString() : "",
      birthWeightGrams: pediatricObj.birth_weight_grams != null ? pediatricObj.birth_weight_grams.toString() : "",
      isPreterm: !!pediatricObj.is_preterm,
      multipleBirth: !!pediatricObj.multiple_birth,
      isDownSyndrome: !!pediatricObj.is_down_syndrome,
    };
    setPediatricData(nextPediatricData);
    setPediatricError(null);

    editSnapshotRef.current = {
      formData: nextFormData,
      emergencyContact: emptyEmergencyContact,
      emergencyContacts: nextEmergencyContacts,
      insuranceData: nextInsuranceData,
      medicalConditions: nextMedicalConditions,
      currentMedications: medicalProfile.current_medications || "",
      previousSurgeries: medicalProfile.previous_surgeries || "",
      lifestyleOptions: nextLifestyleOptions,
      notifications: nextNotifications,
      emailSettings: nextEmailSettings,
      pediatricData: nextPediatricData,
    };
    // Only re-run when the drawer opens or the underlying patient changes —
    // not on every keystroke inside the form.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, patient]);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value };
      if (field === "first_name" || field === "last_name") {
        updated.name = `${field === "first_name" ? value : updated.first_name} ${field === "last_name" ? value : updated.last_name}`;
      }
      return updated;
    });
    if (field === "email") setPersonalEmailError(validateEmail(value));
  };

  const handleEmergencyContactChange = (field: string, value: string) => {
    setEmergencyContact(prev => ({ ...prev, [field]: value }));
    if (field === "email") setEmergencyEmailError(validateEmail(value));
  };

  const toggleMedicalCondition = (id: number) => {
    setMedicalConditions(prev => prev.map(c => (c.id === id ? { ...c, checked: !c.checked } : c)));
  };

  const removeMedicalCondition = (id: number) => {
    setMedicalConditions(prev => prev.filter(c => c.id !== id));
  };

  const toggleLifestyleOption = (id: number) => {
    setLifestyleOptions(prev => prev.map(option => (option.id === id ? { ...option, selected: !option.selected } : option)));
  };

  const addMedicalCondition = () => {
    if (!newConditionName.trim()) return;
    setMedicalConditions(prev => [...prev, { id: Date.now(), name: newConditionName.trim(), checked: false }]);
    setNewConditionName("");
    setShowAddCondition(false);
  };

  const addEmergencyContact = () => {
    if (emergencyContact.email) {
      const emailValidationError = validateEmail(emergencyContact.email);
      if (emailValidationError) {
        toast.error(emailValidationError);
        return;
      }
    }
    // Only name + phone are required — every other field is optional.
    const missingFields: string[] = [];
    if (!emergencyContact.name?.trim()) missingFields.push("Name");
    if (!emergencyContact.mobileNumber?.trim()) missingFields.push("Mobile Number");
    if (missingFields.length > 0) {
      toast.error(`Please fill in: ${missingFields.join(", ")}`);
      return;
    }
    const mobileTrimmed = emergencyContact.mobileNumber.trim();
    if (!indianMobileRegex.test(mobileTrimmed)) {
      toast.error("Please enter a valid 10-digit Indian mobile number (starting with 6-9)");
      return;
    }
    // Excludes the contact currently being edited — otherwise saving an
    // edit without changing its own phone number would always false-flag
    // as a duplicate of itself.
    const isDuplicate = emergencyContacts.some(
      c => c.id !== editingContactId && c.mobileNumber.trim() === mobileTrimmed
    );
    if (isDuplicate) {
      toast.error("An emergency contact with this mobile number has already been added.");
      return;
    }
    if (editingContactId !== null) {
      setEmergencyContacts(prev =>
        prev.map(c => (c.id === editingContactId ? { id: editingContactId, ...emergencyContact } : c))
      );
      setEditingContactId(null);
      toast.success("Emergency contact updated");
    } else {
      setEmergencyContacts(prev => [...prev, { id: Date.now(), ...emergencyContact }]);
      toast.success("Emergency contact added");
    }
    setEmergencyContact({ name: "", relationship: "", email: "", mobileNumber: "", address: "", city: "", state: "", pincode: "", isGuardian: false, guardianWhatsappConsent: false });
    setEmergencyEmailError("");
  };

  const startEditContact = (contact: typeof emergencyContacts[number]) => {
    const { id, ...fields } = contact;
    setEmergencyContact(fields);
    setEditingContactId(id);
  };

  const cancelEditContact = () => {
    setEditingContactId(null);
    setEmergencyContact({ name: "", relationship: "", email: "", mobileNumber: "", address: "", city: "", state: "", pincode: "", isGuardian: false, guardianWhatsappConsent: false });
    setEmergencyEmailError("");
  };

  const removeEmergencyContact = (id: number) => {
    setEmergencyContacts(prev => prev.filter(c => c.id !== id));
    if (editingContactId === id) cancelEditContact();
  };

  // Discard unsaved edits by restoring the opening snapshot, then close.
  const handleCancel = () => {
    const snap = editSnapshotRef.current;
    if (snap) {
      setFormData(snap.formData);
      setEmergencyContact(snap.emergencyContact);
      setEmergencyContacts(snap.emergencyContacts);
      setInsuranceData(snap.insuranceData);
      setMedicalConditions(snap.medicalConditions);
      setCurrentMedications(snap.currentMedications);
      setPreviousSurgeries(snap.previousSurgeries);
      setLifestyleOptions(snap.lifestyleOptions);
      setNotifications(snap.notifications);
      setEmailSettings(snap.emailSettings);
      setPediatricData(snap.pediatricData);
    }
    setPersonalEmailError("");
    setEmergencyEmailError("");
    setEditingContactId(null);
    setPediatricError(null);
    onClose();
  };

  const handleSave = async () => {
    if (!patientId || !originalData) return;

    // Guards against emergency-contact data that never went through
    // addEmergencyContact's own checks (e.g. a future in-place edit path,
    // or a merge from offline sync) - same rules, checked again here.
    //
    // Only applies to contacts that are new or edited this session — a
    // contact unchanged from what the server already has (loaded via the
    // effect above, id assigned as index+1) must not be re-validated
    // against this newer, stricter, frontend-only format: the backend
    // accepts a broader set (e.g. Indian landlines, non-Indian numbers)
    // than this regex allows, so re-checking untouched data could make an
    // existing patient with a valid-but-non-mobile number permanently
    // unsaveable on any edit, not just one to their emergency contacts.
    const originalEmergencyRaw: any[] = originalData.emergency_contacts || [];
    const invalidContact = emergencyContacts.find(c => {
      const mobile = c.mobileNumber.trim();
      if (!mobile || indianMobileRegex.test(mobile)) return false;
      const original = originalEmergencyRaw[c.id - 1];
      const originalMobile = (original?.contact_number || "").replace("+91", "");
      return mobile !== originalMobile;
    });
    if (invalidContact) {
      toast.error(`Emergency contact "${invalidContact.name || "unnamed"}" has an invalid mobile number.`);
      return;
    }
    const seenMobiles = new Set<string>();
    const duplicateContact = emergencyContacts.find(c => {
      const mobile = c.mobileNumber.trim();
      if (!mobile) return false;
      if (seenMobiles.has(mobile)) return true;
      seenMobiles.add(mobile);
      return false;
    });
    if (duplicateContact) {
      toast.error(`Duplicate emergency contact mobile number: ${duplicateContact.mobileNumber}.`);
      return;
    }

    if (pediatricData.isPreterm && !pediatricData.gestationalAgeWeeks) {
      setPediatricError("Gestational age (weeks) is required for a preterm patient");
      toast.error("Gestational age (weeks) is required for a preterm patient");
      return;
    }

    const payload: any = {};

    const patientChanges: any = {};
    if (hasFieldChanged(originalData.patient?.title, formData.title)) patientChanges.title = formData.title;
    if (hasFieldChanged(originalData.patient?.first_name, formData.first_name)) patientChanges.first_name = formData.first_name;
    if (hasFieldChanged(originalData.patient?.last_name, formData.last_name)) patientChanges.last_name = formData.last_name;
    if (hasFieldChanged(originalData.patient?.date_of_birth, formData.dateOfBirth)) patientChanges.date_of_birth = formData.dateOfBirth;
    if (hasFieldChanged(originalData.patient?.gender, formData.gender)) patientChanges.gender = formData.gender;
    if (hasFieldChanged(originalData.patient?.blood_group, formData.bloodGroup)) patientChanges.blood_group = formData.bloodGroup;
    if (hasFieldChanged(originalData.patient?.marital_status, formData.maritalStatus)) patientChanges.marital_status = formData.maritalStatus;
    if (hasFieldChanged(originalData.patient?.occupation, formData.occupation)) patientChanges.occupation = formData.occupation;
    if (hasFieldChanged(originalData.patient?.company, formData.company)) patientChanges.company = formData.company;
    if (Object.keys(patientChanges).length > 0) payload.patients = patientChanges;

    const contactChanges: any = {};
    const originalContact = originalData.contact || {};
    const currentMobile = formData.mobileNumber ? `+91${formData.mobileNumber.replace(/^\+91/, "")}` : "";
    if (hasFieldChanged(originalContact.contact_number, currentMobile)) contactChanges.contact_number = currentMobile;
    if (hasFieldChanged(originalContact.email, formData.email)) contactChanges.email = formData.email;
    const currentAltMobile = formData.alternateNumber ? `+91${formData.alternateNumber.replace(/^\+91/, "")}` : "";
    if (hasFieldChanged(originalContact.alternate_contact_number, currentAltMobile)) contactChanges.alternate_contact_number = currentAltMobile;
    if (hasFieldChanged(originalContact.aadhar_number, formData.aadhar_number)) contactChanges.aadhar_number = formData.aadhar_number;
    if (hasFieldChanged(originalContact.abha_id, formData.abhaAddress)) contactChanges.abha_id = formData.abhaAddress;
    if (Object.keys(contactChanges).length > 0) payload.patient_contacts = contactChanges;

    const addressChanges: any = {};
    const originalAddress = originalData.address || {};
    if (hasFieldChanged(originalAddress.address, formData.address)) addressChanges.address = formData.address;
    if (hasFieldChanged(originalAddress.city, formData.city)) addressChanges.city = formData.city;
    if (hasFieldChanged(originalAddress.state, formData.state)) addressChanges.state = formData.state;
    if (hasFieldChanged(originalAddress.pincode, formData.pincode)) addressChanges.pincode = formData.pincode;
    if (Object.keys(addressChanges).length > 0) payload.patient_addresses = addressChanges;

    const insuranceChanges: any = {};
    const originalInsurance = originalData.insurance || {};
    if (hasFieldChanged(originalInsurance.provider, insuranceData.provider)) insuranceChanges.provider = insuranceData.provider;
    if (hasFieldChanged(originalInsurance.policy_number, insuranceData.policyNumber)) insuranceChanges.policy_number = insuranceData.policyNumber;
    if (hasFieldChanged(originalInsurance.policy_holder_name, insuranceData.policyHolderName)) insuranceChanges.policy_holder_name = insuranceData.policyHolderName;
    if (hasFieldChanged(originalInsurance.policy_expiry_date, insuranceData.policyExpiryDate)) insuranceChanges.policy_expiry_date = insuranceData.policyExpiryDate;
    if (hasFieldChanged(originalInsurance.coverage_amount?.toString(), insuranceData.coverageAmount)) insuranceChanges.coverage_amount = insuranceData.coverageAmount;
    if (Object.keys(insuranceChanges).length > 0) payload.insurance = insuranceChanges;

    const medicalChanges: any = {};
    const originalMedical = originalData.medical_profile || {};
    if (hasFieldChanged(originalMedical.current_medications, currentMedications)) medicalChanges.current_medications = currentMedications;
    if (hasFieldChanged(originalMedical.previous_surgeries, previousSurgeries)) medicalChanges.previous_surgeries = previousSurgeries;
    const currentConditions = medicalConditions.map(c => ({ [c.name]: c.checked ? "Yes" : "No" }));
    const originalConditions = originalMedical.medical_conditions
      ? (typeof originalMedical.medical_conditions === "string" ? JSON.parse(originalMedical.medical_conditions) : originalMedical.medical_conditions)
      : [];
    if (hasFieldChanged(JSON.stringify(originalConditions), JSON.stringify(currentConditions)))
      medicalChanges.medical_conditions = currentConditions.length > 0 ? JSON.stringify(currentConditions) : null;
    const selectedLifestyles = lifestyleOptions.filter(opt => opt.selected).map(opt => opt.label);
    const originalLifestyle = originalMedical.lifestyle_options
      ? (typeof originalMedical.lifestyle_options === "string" ? JSON.parse(originalMedical.lifestyle_options) : originalMedical.lifestyle_options)
      : [];
    if (hasFieldChanged(originalLifestyle, selectedLifestyles))
      medicalChanges.lifestyle_options = selectedLifestyles.length > 0 ? JSON.stringify(selectedLifestyles) : null;
    if (Object.keys(medicalChanges).length > 0) payload.medical_profiles = medicalChanges;

    const pediatricChanges: any = {};
    const originalPediatric = originalData.pediatric_profile || {};
    const gaWeeksNum = pediatricData.gestationalAgeWeeks ? Number(pediatricData.gestationalAgeWeeks) : null;
    const gaDaysNum = pediatricData.gestationalAgeDays ? Number(pediatricData.gestationalAgeDays) : null;
    const birthWeightNum = pediatricData.birthWeightGrams ? Number(pediatricData.birthWeightGrams) : null;
    if (hasFieldChanged(originalPediatric.gestational_age_weeks ?? null, gaWeeksNum))
      pediatricChanges.gestational_age_weeks = gaWeeksNum;
    if (hasFieldChanged(originalPediatric.gestational_age_days ?? null, gaDaysNum))
      pediatricChanges.gestational_age_days = gaDaysNum;
    if (hasFieldChanged(originalPediatric.birth_weight_grams ?? null, birthWeightNum))
      pediatricChanges.birth_weight_grams = birthWeightNum;
    if (hasFieldChanged(!!originalPediatric.is_preterm, pediatricData.isPreterm))
      pediatricChanges.is_preterm = pediatricData.isPreterm;
    if (hasFieldChanged(!!originalPediatric.multiple_birth, pediatricData.multipleBirth))
      pediatricChanges.multiple_birth = pediatricData.multipleBirth;
    if (hasFieldChanged(!!originalPediatric.is_down_syndrome, pediatricData.isDownSyndrome))
      pediatricChanges.is_down_syndrome = pediatricData.isDownSyndrome;
    if (Object.keys(pediatricChanges).length > 0) payload.patient_pediatric_profile = pediatricChanges;

    const preferencesChanges: any = {};
    const originalPrefs = originalData.preferences || {};
    if (hasFieldChanged(originalPrefs.appointment_confirmations, notifications.appointmentConfirmations)) preferencesChanges.appointment_confirmations = notifications.appointmentConfirmations;
    if (hasFieldChanged(originalPrefs.appointment_reminders, notifications.appointmentReminders)) preferencesChanges.appointment_reminders = notifications.appointmentReminders;
    if (hasFieldChanged(originalPrefs.prescription_ready, notifications.prescriptionReady)) preferencesChanges.prescription_ready = notifications.prescriptionReady;
    if (hasFieldChanged(originalPrefs.payment_received, notifications.paymentReceived)) preferencesChanges.payment_received = notifications.paymentReceived;
    if (hasFieldChanged(originalPrefs.payment_reminders, notifications.paymentReminders)) preferencesChanges.payment_reminders = notifications.paymentReminders;
    if (hasFieldChanged(originalPrefs.system_updates, notifications.systemUpdates)) preferencesChanges.system_updates = notifications.systemUpdates;
    if (hasFieldChanged(originalPrefs.email_frequency, emailSettings.frequency)) preferencesChanges.email_frequency = emailSettings.frequency;
    if (hasFieldChanged(originalPrefs.quiet_hours, emailSettings.quietHours)) preferencesChanges.quiet_hours = emailSettings.quietHours;
    if (Object.keys(preferencesChanges).length > 0) payload.patient_preferences = preferencesChanges;

    const currentEmergency = emergencyContacts.map(contact => ({
      name: contact.name,
      relationship: contact.relationship || undefined,
      email: contact.email || undefined,
      contact_number: contact.mobileNumber ? `+91${contact.mobileNumber.replace(/^\+91/, "")}` : undefined,
      address: contact.address || undefined,
      city: contact.city || undefined,
      state: contact.state || undefined,
      pincode: contact.pincode || undefined,
      is_guardian: contact.isGuardian,
      guardian_whatsapp_consent: contact.isGuardian && contact.guardianWhatsappConsent,
    }));
    const originalEmergency = originalData.emergency_contacts || [];
    if (hasFieldChanged(originalEmergency, currentEmergency)) payload.emergency_contacts = currentEmergency;

    if (Object.keys(payload).length === 0) {
      toast.info("No changes detected");
      onClose();
      return;
    }

    setSaving(true);

    // Uses `payload` (the complete grouped PATCH body) as syncPayload so the
    // push engine sends exactly what the server's PatientPatchUpdate expects.
    const offlineSave = async () => {
      const contactNumber = formData.mobileNumber
        ? `+91${formData.mobileNumber.replace(/^\+91/, "")}`
        : (originalData?.contact?.contact_number ?? "");
      const version = (patientData as any)?.version ?? 1;
      await putAndQueue<PatientRecord>(
        "patient",
        patientId,
        {
          patient_id: patientId,
          first_name: formData.first_name,
          last_name: formData.last_name,
          date_of_birth: formData.dateOfBirth,
          gender: formData.gender as PatientRecord["gender"],
          contact_number: contactNumber,
          blood_group: formData.bloodGroup || null,
          marital_status: formData.maritalStatus || null,
          occupation: formData.occupation || null,
          company: formData.company || null,
          is_active: true,
          email: formData.email || null,
          aadhar_number: formData.aadhar_number || null,
          abha_id: formData.abhaAddress || null,
          alternate_contact_number: formData.alternateNumber ? `+91${formData.alternateNumber.replace(/^\+91/, "")}` : null,
          address: formData.address || null,
          city: formData.city || null,
          state: formData.state || null,
          pincode: formData.pincode || null,
        },
        "update",
        version,
        { ...payload, version },
      );
      toast.info("You're offline — changes saved locally and will sync when connected.");
    };

    if (!navigator.onLine) {
      try { await offlineSave(); onClose(); }
      catch { toast.error("Failed to save offline. Please try again."); }
      setSaving(false);
      return;
    }

    logger.info("Submitting patient update", { patient_id: patientId, field_count: Object.keys(payload).length });

    try {
      const response = await PatientAPI.update(patientId, payload);
      setPatientData(response);
      toast.success("Patient updated successfully!");
      onSaved(response);
      onClose();
    } catch (error) {
      console.error("Error updating patient:", error);
      if (isBackendUnreachable(error)) {
        try { await offlineSave(); onClose(); }
        catch { toast.error("Failed to save offline. Please try again."); }
      } else {
        logger.error("Error updating patient", error instanceof Error ? error : undefined, { patient_id: patientId });
        // A 403 is a refusal, not a failure — show the server's reason naming the
        // missing grant rather than "Failed to update patient: HTTP 403", which
        // leaves the user retrying something that can never succeed.
        if (isPermissionDenied(error)) toast.error(actionErrorMessage(error, "Failed to update patient"));
        else if (error instanceof PatientApiError) toast.error(error.message);
        else if (error instanceof Error) toast.error(`Failed to update patient: ${error.message}`);
        else toast.error("An unexpected error occurred. Please try again.");
      }
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e: KeyboardEvent) => { if (e.key === "Escape") handleCancel(); };
    document.addEventListener("keydown", onKeyDown);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = "";
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Editing lost its own route when it became a drawer, so patients.update is
  // enforced here as well as on the Edit button that opens it — an isOpen forced
  // any other way (a stale render, a future caller) must not get the form.
  // Legacy fallback true: pre-RBAC every clinic role could edit, and the PATCH
  // is enforced server-side regardless.
  if (!isOpen || !canOr("patients.update", true)) return null;

  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={handleCancel} />
      <div className="absolute inset-y-0 right-0 w-full sm:w-[640px] bg-[#f5f5f7] shadow-xl flex flex-col">
        <div className="flex items-center justify-between gap-3 border-b border-slate-200 bg-white px-4 md:px-6 py-4">
          <div>
            <h2 className="text-base md:text-lg font-semibold text-slate-800">Edit Patient</h2>
            <p className="text-xs text-slate-500">{formData.name}</p>
          </div>
          <button
            onClick={handleCancel}
            className="flex h-8 w-8 items-center justify-center rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-600"
            aria-label="Close"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          </button>
        </div>

        <div className="flex gap-1 md:gap-2 overflow-x-auto scrollbar-hide px-4 md:px-6 py-3 bg-white border-b border-slate-200">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-3 md:px-4 py-2 text-xs md:text-sm font-medium whitespace-nowrap flex-shrink-0 rounded-lg ${
                activeTab === tab.id ? "text-white" : "text-slate-500 hover:text-slate-700"
              }`}
              style={activeTab === tab.id ? { background: "linear-gradient(90deg, #003EC8 0%, #2B7FFF 100%)" } : undefined}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto px-4 md:px-6 py-4 md:py-6">
          {activeTab === "personal" && (
            <>
              <div className="flex flex-col gap-4 lg:flex-row">
                <div className="flex-1 rounded-2xl border border-slate-200 bg-white p-4 md:p-6">
                  <div className="mb-4 flex items-center gap-3">
                    <BasicDetailsIcon />
                    <h3 className="text-sm md:text-base font-semibold text-slate-800">Basic Details</h3>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3">
                    <div>
                      <label className="mb-1.5 block text-sm font-medium text-slate-600">First Name</label>
                      <input
                        type="text"
                        value={formData.first_name}
                        onChange={(e) => handleInputChange("first_name", e.target.value)}
                        className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-700"
                      />
                    </div>
                    <div>
                      <label className="mb-1.5 block text-sm font-medium text-slate-600">Last Name</label>
                      <input
                        type="text"
                        value={formData.last_name}
                        onChange={(e) => handleInputChange("last_name", e.target.value)}
                        className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-700"
                      />
                    </div>
                    <div>
                      <label className="mb-1.5 block text-sm font-medium text-slate-600">
                        Date of Birth <span className="text-red-500">*</span>
                      </label>
                      <DateInput
                        value={formData.dateOfBirth}
                        onChange={(e) => handleInputChange("dateOfBirth", e.target.value)}
                        className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-700"
                      />
                    </div>
                    <div>
                      <label className="mb-1.5 block text-sm font-medium text-slate-600">Marital status</label>
                      <div className="relative">
                        <select
                          value={formData.maritalStatus}
                          onChange={(e) => handleInputChange("maritalStatus", e.target.value)}
                          className="w-full appearance-none rounded-lg border border-slate-200 bg-white px-3 py-2.5 pr-10 text-sm text-slate-700"
                        >
                          <option value="Single">Single</option>
                          <option value="Married">Married</option>
                          <option value="Divorced">Divorced</option>
                          <option value="Widowed">Widowed</option>
                        </select>
                        <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"><ChevronDownIcon /></div>
                      </div>
                    </div>
                    <div>
                      <label className="mb-1.5 block text-sm font-medium text-slate-600">Blood Group</label>
                      <div className="relative">
                        <select
                          value={formData.bloodGroup}
                          onChange={(e) => handleInputChange("bloodGroup", e.target.value)}
                          className="w-full appearance-none rounded-lg border border-slate-200 bg-white px-3 py-2.5 pr-10 text-sm text-slate-700"
                        >
                          <option value="">Select blood group</option>
                          {["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"].map(bg => (
                            <option key={bg} value={bg}>{bg}</option>
                          ))}
                        </select>
                        <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"><ChevronDownIcon /></div>
                      </div>
                    </div>
                    <div>
                      <label className="mb-1.5 block text-sm font-medium text-slate-600">
                        Gender <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <select
                          value={formData.gender}
                          onChange={(e) => handleInputChange("gender", e.target.value)}
                          className="w-full appearance-none rounded-lg border border-slate-200 bg-white px-3 py-2.5 pr-10 text-sm text-slate-700"
                        >
                          <option value="">Select gender</option>
                          <option value="Male">Male</option>
                          <option value="Female">Female</option>
                          <option value="Other">Other</option>
                        </select>
                        <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"><ChevronDownIcon /></div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="w-full lg:w-72 rounded-2xl border border-slate-200 bg-white p-4 md:p-6">
                  <div className="mb-4 flex items-center gap-3">
                    <ContactInfoIcon />
                    <h3 className="text-sm md:text-base font-semibold text-slate-800">Contact Information</h3>
                  </div>
                  <div className="space-y-3">
                    <div>
                      <label className="mb-1.5 block text-sm font-medium text-slate-600">
                        Mobile Number <span className="text-red-500">*</span>
                      </label>
                      <div className="flex rounded-lg border border-slate-200 bg-white overflow-hidden focus-within:border-cyan-400 focus-within:ring-1 focus-within:ring-cyan-400">
                        <span className="flex items-center px-3 text-sm font-medium text-slate-500 bg-slate-50 border-r border-slate-200 shrink-0">🇮🇳 +91</span>
                        <input
                          type="text"
                          value={formData.mobileNumber}
                          onChange={(e) => handleInputChange("mobileNumber", e.target.value)}
                          className="flex-1 min-w-0 bg-transparent px-3 py-2.5 text-sm text-slate-700 outline-none"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="mb-1.5 block text-sm font-medium text-slate-600">Alternate Number</label>
                      <div className="flex rounded-lg border border-slate-200 bg-white overflow-hidden focus-within:border-cyan-400 focus-within:ring-1 focus-within:ring-cyan-400">
                        <span className="flex items-center px-3 text-sm font-medium text-slate-500 bg-slate-50 border-r border-slate-200 shrink-0">🇮🇳 +91</span>
                        <input
                          type="text"
                          value={formData.alternateNumber}
                          onChange={(e) => handleInputChange("alternateNumber", e.target.value)}
                          className="flex-1 min-w-0 bg-transparent px-3 py-2.5 text-sm text-slate-700 outline-none"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="mb-1.5 block text-sm font-medium text-slate-600">Email ID</label>
                      <input
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleInputChange("email", e.target.value)}
                        className={`w-full rounded-lg border ${personalEmailError ? "border-red-400" : "border-slate-200"} bg-white px-3 py-2.5 text-sm text-slate-700`}
                      />
                      {personalEmailError && <p className="mt-1.5 text-xs text-red-600">{personalEmailError}</p>}
                    </div>
                    <div>
                      <label className="mb-1.5 block text-sm font-medium text-slate-600">Aadhaar Number</label>
                      <input
                        type="text"
                        value={formData.aadhar_number}
                        onChange={(e) => handleInputChange("aadhar_number", e.target.value)}
                        className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-700"
                      />
                    </div>
                    <div>
                      <label className="mb-1.5 block text-sm font-medium text-slate-600">ABHA Address</label>
                      <input
                        type="text"
                        value={formData.abhaAddress}
                        onChange={(e) => handleInputChange("abhaAddress", e.target.value)}
                        className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-700"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {isPediatricAge && (
                <div className="mt-4 w-full rounded-2xl border border-cyan-100 bg-cyan-50/40 p-4 md:p-6">
                  <h3 className="mb-4 text-sm md:text-base font-semibold text-slate-800">
                    Neonatal Information (growth-chart tracking)
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="col-span-2 md:col-span-4 flex items-center gap-2">
                      <label className="flex items-center gap-2 text-sm text-slate-700">
                        <input
                          type="checkbox"
                          checked={pediatricData.isPreterm}
                          onChange={(e) => setPediatricData((prev) => ({ ...prev, isPreterm: e.target.checked }))}
                          className="h-4 w-4 rounded border-slate-300 text-cyan-600 focus:ring-cyan-400"
                        />
                        Born preterm (before 37 weeks gestation)
                      </label>
                    </div>
                    <div>
                      <label className="mb-1.5 block text-sm font-medium text-slate-600">
                        GA (weeks){pediatricData.isPreterm && <span className="text-red-500"> *</span>}
                      </label>
                      <input
                        type="number"
                        min={20}
                        max={45}
                        value={pediatricData.gestationalAgeWeeks}
                        onChange={(e) => setPediatricData((prev) => ({ ...prev, gestationalAgeWeeks: e.target.value }))}
                        className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-700"
                      />
                    </div>
                    <div>
                      <label className="mb-1.5 block text-sm font-medium text-slate-600">GA (days)</label>
                      <input
                        type="number"
                        min={0}
                        max={6}
                        value={pediatricData.gestationalAgeDays}
                        onChange={(e) => setPediatricData((prev) => ({ ...prev, gestationalAgeDays: e.target.value }))}
                        className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-700"
                      />
                    </div>
                    <div>
                      <label className="mb-1.5 block text-sm font-medium text-slate-600">Birth weight (g)</label>
                      <input
                        type="number"
                        min={200}
                        max={6000}
                        value={pediatricData.birthWeightGrams}
                        onChange={(e) => setPediatricData((prev) => ({ ...prev, birthWeightGrams: e.target.value }))}
                        className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-700"
                      />
                    </div>
                    <div className="flex items-end pb-2.5">
                      <label className="flex items-center gap-2 text-sm text-slate-700">
                        <input
                          type="checkbox"
                          checked={pediatricData.multipleBirth}
                          onChange={(e) => setPediatricData((prev) => ({ ...prev, multipleBirth: e.target.checked }))}
                          className="h-4 w-4 rounded border-slate-300 text-cyan-600 focus:ring-cyan-400"
                        />
                        Multiple birth
                      </label>
                    </div>
                    <div className="flex items-end pb-2.5">
                      <label className="flex items-center gap-2 text-sm text-slate-700">
                        <input
                          type="checkbox"
                          checked={pediatricData.isDownSyndrome}
                          onChange={(e) => setPediatricData((prev) => ({ ...prev, isDownSyndrome: e.target.checked }))}
                          className="h-4 w-4 rounded border-slate-300 text-cyan-600 focus:ring-cyan-400"
                        />
                        Down syndrome
                      </label>
                    </div>
                  </div>
                  {pediatricError && <p className="mt-2 text-xs text-red-600">{pediatricError}</p>}
                </div>
              )}

              <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-4 md:p-6">
                <div className="mb-4 flex items-center gap-3">
                  <AddressIcon />
                  <h3 className="text-sm md:text-base font-semibold text-slate-800">Address</h3>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-slate-600">Address</label>
                    <input
                      type="text"
                      value={formData.address}
                      onChange={(e) => handleInputChange("address", e.target.value)}
                      className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-700"
                    />
                  </div>
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-slate-600">City</label>
                    <input
                      type="text"
                      value={formData.city}
                      onChange={(e) => handleInputChange("city", e.target.value)}
                      placeholder="Enter city"
                      className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-700"
                    />
                  </div>
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-slate-600">State</label>
                    <div className="relative">
                      <select
                        value={formData.state}
                        onChange={(e) => handleInputChange("state", e.target.value)}
                        className="w-full appearance-none rounded-lg border border-slate-200 bg-white px-3 py-2.5 pr-10 text-sm text-slate-700"
                      >
                        <option value="">Select state</option>
                        {INDIAN_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                      <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"><ChevronDownIcon /></div>
                    </div>
                  </div>
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-slate-600">Pincode</label>
                    <input
                      type="text"
                      value={formData.pincode}
                      onChange={(e) => {
                        const pin = e.target.value.replace(/\D/g, "").slice(0, 6);
                        handleInputChange("pincode", pin);
                        lookupPincode(pin, (city) => handleInputChange("city", city), (state) => handleInputChange("state", state));
                      }}
                      placeholder="6-digit pincode"
                      maxLength={6}
                      className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-700"
                    />
                  </div>
                </div>
              </div>
            </>
          )}

          {activeTab === "family" && (
            <div className="flex flex-col gap-4 lg:flex-row">
              <div className="flex-1 rounded-2xl border border-slate-200 bg-white p-4 md:p-6">
                <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <h3 className="text-base font-semibold text-slate-800">
                    {editingContactId !== null ? "Edit Emergency Contact" : "Emergency Contact Details"}
                  </h3>
                  <div className="flex gap-2 w-full sm:w-auto">
                    {editingContactId !== null && (
                      <button
                        onClick={cancelEditContact}
                        aria-label="Cancel editing contact"
                        className="rounded-lg border border-slate-200 px-4 py-2 text-xs font-medium text-slate-600 hover:bg-slate-50 w-full sm:w-auto"
                      >
                        Cancel
                      </button>
                    )}
                    <button
                      onClick={addEmergencyContact}
                      className="rounded-lg px-4 py-2 text-xs font-medium text-white w-full sm:w-auto"
                      style={{ background: "linear-gradient(90deg, #00B8DB 0%, #2B7FFF 100%)" }}
                    >
                      {editingContactId !== null ? "Update Contact" : "Add"}
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3">
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-slate-600">Name <span className="text-red-500">*</span></label>
                    <input
                      type="text"
                      value={emergencyContact.name}
                      onChange={(e) => handleEmergencyContactChange("name", e.target.value)}
                      placeholder="Rajesh Kumar"
                      className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-700 placeholder:text-slate-400"
                    />
                  </div>
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-slate-600">Relationship</label>
                    <div className="relative">
                      <select
                        value={emergencyContact.relationship}
                        onChange={(e) => handleEmergencyContactChange("relationship", e.target.value)}
                        className="w-full appearance-none rounded-lg border border-slate-200 bg-white px-3 py-2.5 pr-10 text-sm text-slate-700"
                      >
                        <option value="">Select</option>
                        <option value="Father">Father</option>
                        <option value="Mother">Mother</option>
                        <option value="Spouse">Spouse</option>
                        <option value="Sibling">Sibling</option>
                        <option value="Child">Child</option>
                        <option value="Friend">Friend</option>
                        <option value="Other">Other</option>
                      </select>
                      <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"><ChevronDownIcon /></div>
                    </div>
                  </div>
                  {/* Full width (not squeezed into a half-width grid cell like
                      the rest) — the flag+code prefix was eating most of the
                      space, leaving barely 2 digits of the number visible. */}
                  <div className="sm:col-span-2">
                    <label className="mb-1.5 block text-sm font-medium text-slate-600">Mobile Number <span className="text-red-500">*</span></label>
                    <div className="flex rounded-lg border border-slate-200 bg-white overflow-hidden focus-within:border-cyan-400 focus-within:ring-1 focus-within:ring-cyan-400">
                      <span className="flex items-center px-3 text-sm font-medium text-slate-500 bg-slate-50 border-r border-slate-200 shrink-0">🇮🇳 +91</span>
                      <input
                        type="text"
                        value={emergencyContact.mobileNumber}
                        onChange={(e) => handleEmergencyContactChange("mobileNumber", e.target.value)}
                        placeholder="98765 43210"
                        className="flex-1 min-w-0 bg-transparent px-3 py-2.5 text-sm text-slate-700 placeholder:text-slate-400 outline-none"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-slate-600">E-Mail</label>
                    <input
                      type="email"
                      value={emergencyContact.email}
                      onChange={(e) => handleEmergencyContactChange("email", e.target.value)}
                      placeholder="patient@email.com"
                      className={`w-full rounded-lg border ${emergencyEmailError ? "border-red-400" : "border-slate-200"} bg-white px-3 py-2.5 text-sm text-slate-700 placeholder:text-slate-400`}
                    />
                    {emergencyEmailError && <p className="mt-1.5 text-xs text-red-600">{emergencyEmailError}</p>}
                  </div>
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-slate-600">Address</label>
                    <input
                      type="text"
                      value={emergencyContact.address}
                      onChange={(e) => handleEmergencyContactChange("address", e.target.value)}
                      placeholder="Street address, Building name"
                      className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-700 placeholder:text-slate-400"
                    />
                  </div>
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-slate-600">City</label>
                    <input
                      type="text"
                      value={emergencyContact.city}
                      onChange={(e) => handleEmergencyContactChange("city", e.target.value)}
                      placeholder="Enter city"
                      className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-700 placeholder:text-slate-400"
                    />
                  </div>
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-slate-600">State</label>
                    <div className="relative">
                      <select
                        value={emergencyContact.state}
                        onChange={(e) => handleEmergencyContactChange("state", e.target.value)}
                        className="w-full appearance-none rounded-lg border border-slate-200 bg-white px-3 py-2.5 pr-10 text-sm text-slate-700"
                      >
                        <option value="">Select state</option>
                        {INDIAN_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                      <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"><ChevronDownIcon /></div>
                    </div>
                  </div>
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-slate-600">Pincode</label>
                    <input
                      type="text"
                      value={emergencyContact.pincode}
                      onChange={(e) => {
                        const pin = e.target.value.replace(/\D/g, "").slice(0, 6);
                        handleEmergencyContactChange("pincode", pin);
                        lookupPincode(pin, (city) => handleEmergencyContactChange("city", city), (state) => handleEmergencyContactChange("state", state));
                      }}
                      placeholder="6-digit pincode"
                      maxLength={6}
                      className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-700 placeholder:text-slate-400"
                    />
                  </div>
                  {isPediatricAge && (
                    <div className="sm:col-span-2 rounded-xl border border-cyan-100 bg-cyan-50/40 p-3 space-y-2">
                      <label className="flex items-center gap-2 text-sm text-slate-700">
                        <input
                          type="checkbox"
                          checked={emergencyContact.isGuardian}
                          onChange={(e) => {
                            const isGuardian = e.target.checked;
                            setEmergencyContact((prev) => ({
                              ...prev,
                              isGuardian,
                              guardianWhatsappConsent: isGuardian ? prev.guardianWhatsappConsent : false,
                            }));
                          }}
                          className="h-4 w-4 rounded border-slate-300 text-cyan-600 focus:ring-cyan-400"
                        />
                        Guardian (eligible to receive this child's growth-chart share)
                      </label>
                      {emergencyContact.isGuardian && (
                        <label className="flex items-center gap-2 pl-6 text-sm text-slate-700">
                          <input
                            type="checkbox"
                            checked={emergencyContact.guardianWhatsappConsent}
                            onChange={(e) => setEmergencyContact((prev) => ({ ...prev, guardianWhatsappConsent: e.target.checked }))}
                            className="h-4 w-4 rounded border-slate-300 text-cyan-600 focus:ring-cyan-400"
                          />
                          Consents to receive growth-chart updates via WhatsApp
                        </label>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="w-full lg:w-72 rounded-2xl border border-slate-200 bg-white p-4 md:p-6">
                <h3 className="mb-4 text-base font-semibold text-slate-800">List of Added Contacts</h3>
                <div className="space-y-3">
                  {emergencyContacts.map((contact) => (
                    <div
                      key={contact.id}
                      className={`flex items-center justify-between rounded-lg border px-4 py-3 ${
                        editingContactId === contact.id ? "border-cyan-400 bg-cyan-50" : "border-slate-200 bg-slate-50"
                      }`}
                    >
                      <div className="min-w-0">
                        <p className="font-medium text-slate-800 truncate flex items-center gap-1.5">
                          {contact.name}
                          {contact.isGuardian && (
                            <span className="shrink-0 rounded-full bg-cyan-100 px-1.5 py-0.5 text-[10px] font-medium text-cyan-700">
                              Guardian
                            </span>
                          )}
                        </p>
                        <p className="text-sm text-slate-500 truncate">{contact.relationship || contact.mobileNumber}</p>
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        <button
                          onClick={() => startEditContact(contact)}
                          aria-label={`Edit ${contact.name}`}
                          className="flex h-6 w-6 items-center justify-center rounded-full border border-slate-300 text-slate-400 hover:border-slate-400 hover:text-slate-600"
                        >
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                            <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => removeEmergencyContact(contact.id)}
                          aria-label={`Remove ${contact.name}`}
                          className="flex h-6 w-6 items-center justify-center rounded-full border border-slate-300 text-slate-400 hover:border-slate-400 hover:text-slate-600"
                        >
                          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M9 3L3 9M3 3L9 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === "medical" && (
            <div className="space-y-4">
              <div className="rounded-2xl border border-slate-200 bg-white p-4 md:p-6">
                <h3 className="mb-4 text-base font-semibold text-slate-800">Medical Information</h3>

                <div className="mb-4">
                  <h4 className="mb-3 text-sm font-semibold text-slate-800">Medical Conditions</h4>
                  <div className="flex flex-wrap gap-3">
                    {showAddCondition ? (
                      <div className="flex items-center gap-2 rounded-lg px-4 py-2 min-w-[200px]" style={{ border: "1px solid #CEFAFE", borderRadius: "20px" }}>
                        <input
                          type="text"
                          value={newConditionName}
                          onChange={(e) => setNewConditionName(e.target.value)}
                          onKeyDown={(e) => e.key === "Enter" && addMedicalCondition()}
                          placeholder="Enter condition name"
                          className="flex-1 bg-transparent text-sm text-slate-700 placeholder:text-slate-400 outline-none"
                          autoFocus
                        />
                        <button onClick={addMedicalCondition} className="flex h-7 w-7 items-center justify-center rounded-full text-white" style={{ background: "linear-gradient(90deg, #00B8DB 0%, #2B7FFF 100%)" }}>
                          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M10 3L4.5 8.5L2 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                        </button>
                        <button onClick={() => { setShowAddCondition(false); setNewConditionName(""); }} className="flex h-7 w-7 items-center justify-center rounded-full text-slate-500 hover:bg-slate-100">
                          <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1 1L9 9M1 9L9 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setShowAddCondition(true)}
                        className="flex items-center gap-3 rounded-lg px-4 py-3 text-sm font-medium text-slate-700 hover:bg-slate-50 min-w-[200px]"
                        style={{ border: "1px solid #CEFAFE", borderRadius: "20px" }}
                      >
                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M6 1V11M1 6H11" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
                        Add New
                      </button>
                    )}
                    {medicalConditions.map((condition) => (
                      <div key={condition.id} className="flex items-center justify-between gap-3 rounded-lg px-4 py-3 min-w-[200px]" style={{ border: "1px solid #CEFAFE", borderRadius: "20px" }}>
                        <div className="flex items-center gap-3">
                          <input type="checkbox" checked={condition.checked} onChange={() => toggleMedicalCondition(condition.id)} className="h-4 w-4 cursor-pointer rounded border-slate-300" />
                          <span className="text-sm text-slate-700">{condition.name}</span>
                        </div>
                        <button onClick={() => removeMedicalCondition(condition.id)} className="flex h-6 w-6 items-center justify-center rounded-full text-white hover:opacity-80" style={{ background: "#0000009C" }}>
                          <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1 1L9 9M1 9L9 1" stroke="white" strokeWidth="1.5" strokeLinecap="round"/></svg>
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="mb-4">
                  <h4 className="mb-2 text-sm font-semibold text-slate-800">Current Medications</h4>
                  <textarea
                    value={currentMedications}
                    onChange={(e) => setCurrentMedications(e.target.value)}
                    placeholder="List current medications with dosage"
                    rows={3}
                    className="w-full resize-none rounded-lg bg-white px-3 py-2.5 text-sm text-slate-700 placeholder:text-slate-400"
                    style={{ border: "1px solid #CEFAFE" }}
                  />
                </div>

                <div>
                  <h4 className="mb-2 text-sm font-semibold text-slate-800">Previous Surgeries</h4>
                  <textarea
                    value={previousSurgeries}
                    onChange={(e) => setPreviousSurgeries(e.target.value)}
                    placeholder="List any previous surgeries with dates"
                    rows={3}
                    className="w-full resize-none rounded-lg bg-white px-3 py-2.5 text-sm text-slate-700 placeholder:text-slate-400"
                    style={{ border: "1px solid #CEFAFE" }}
                  />
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-white p-4 md:p-6">
                <h3 className="mb-4 text-base font-semibold text-slate-800">Lifestyle Information</h3>
                <div className="flex flex-wrap gap-3">
                  {lifestyleOptions.map((option) => (
                    <button
                      key={option.id}
                      onClick={() => toggleLifestyleOption(option.id)}
                      className="px-4 py-2 text-sm font-medium transition-colors"
                      style={{
                        background: option.selected ? "#0051FF" : "white",
                        color: option.selected ? "white" : "#64748B",
                        border: option.selected ? "none" : "1px solid #0051FF",
                        borderRadius: "10px",
                      }}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === "allergies" && (
            <AllergiesPanel patientId={patientId} editable={true} />
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-slate-200 bg-white px-4 md:px-6 py-4">
          <button
            onClick={handleCancel}
            disabled={saving}
            className="rounded-lg px-4 py-2 text-sm font-medium text-slate-700 border border-slate-200 bg-white hover:bg-slate-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="rounded-lg px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
            style={{ background: "linear-gradient(90deg, #00B8DB 0%, #2B7FFF 100%)" }}
          >
            {saving ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditPatientDrawer;
